/**
 * Which MCP endpoint a token works against, decided by WHERE THE PORTAL IS
 * RUNNING. A token is minted by that portal's admin-api, so it only opens the
 * MCP tier wired to the same admin-api; the Live/Dev data switch does not
 * change that.
 *
 *   localhost            -> local stack      http://localhost:3100/dev/mcp
 *   *dev*  portal host   -> dev              https://mcp.dev.wpos.ai/dev/mcp
 *   *beta* portal host   -> beta             https://mcp.beta.wpos.ai/beta/mcp
 *   anything else        -> production       https://mcp.wpos.ai/main/mcp
 *
 * The public hostnames follow the portal's own (portal.X -> mcp.X); override
 * any of them at build time with VITE_MCP_URL_LOCAL / _DEV / _BETA / _MAIN.
 */
export type McpEnvName = 'local' | 'dev' | 'beta' | 'main';

export interface McpTarget { name: McpEnvName; label: string; url: string }

const LABEL: Record<McpEnvName, string> = { local: 'Local', dev: 'Dev', beta: 'Beta', main: 'Live' };
/** The path segment the MCP service serves each environment under. */
const TIER: Record<McpEnvName, string> = { local: 'dev', dev: 'dev', beta: 'beta', main: 'main' };

export function mcpEnvOf(hostname: string): McpEnvName {
  const h = hostname.toLowerCase();
  if (h === 'localhost' || h === '127.0.0.1' || h === '::1' || h.endsWith('.localhost')) return 'local';
  if (/(^|[.-])beta([.-]|$)/.test(h)) return 'beta';
  if (/(^|[.-])dev([.-]|$)/.test(h)) return 'dev';
  return 'main';
}

export function mcpTarget(hostname: string, vars: Record<string, string | undefined> = {}): McpTarget {
  const name = mcpEnvOf(hostname);
  const defaults: Record<McpEnvName, string> = {
    local: 'http://localhost:3100',
    dev: 'https://mcp.dev.wpos.ai',
    beta: 'https://mcp.beta.wpos.ai',
    main: 'https://mcp.wpos.ai',
  };
  const base = (vars[`VITE_MCP_URL_${name.toUpperCase()}`] || defaults[name]).replace(/\/+$/, '');
  return { name, label: LABEL[name], url: `${base}/${TIER[name]}/mcp` };
}

export function currentMcpTarget(): McpTarget {
  const host = typeof location !== 'undefined' ? location.hostname : 'localhost';
  return mcpTarget(host, ((import.meta as any).env ?? {}) as Record<string, string | undefined>);
}
